import React, { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import Loader from '../components/Loader';
import MapEditor from '../components/Map/MapEditor';
import { Map } from '../reducers/map/mapReducer';
import {
    addPing,
    removePing,
    resetMap,
    setMapId,
    setMapValues,
} from '../reducers/map/mapReducerActions';
import { preloadImages } from '../utils/images';
import Client from '../utils/network';
import DmLayerEditor from '../components/Map/DmLayerEditor';
import SpellEffectEditor from '../components/Spells/SpellEffectEditor';
import MeasureEditor from '../components/Map/MeasureEditor';
import HostToggle from '../components/Map/HostToggle';
import { useSelector } from 'react-redux';
import { Rootstate } from '../store';
import EditorHotbar from '../components/Map/EditorHotbar';
import TokenSelector from '../components/Token/TokenSelector';
import MapImageSelector from '../components/MapImages/MapImageSelector';
import SpellSelector from '../components/Spells/SpellSelector';
import ZoomEditor from '../components/Map/ZoomEditor';
import ToggleHotbar from '../components/Map/ToggleHotbar';
import FogEditor from '../components/Map/FogEditor';
import TurnOrderWidget from '../components/Map/TurnOrder/TurnOrderWidget';
import { setXTiles, setYTiles } from '../reducers/gridEdit/gridReducerActions';
import { ObjectId } from 'bson';

const ViewMap = () => {
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const eventSourceRef = useRef<EventSource>();

    const { guildId, channelId } = useSelector((state: Rootstate) => ({
        guildId: state.auth.guildId,
        channelId: state.auth.channelId,
    }), shallowEqual);

    useEffect(() => {
        dispatch(resetMap());
        if(!id) {
            return;
        }
        Client.request(`/api/map/${id}`).then(async (res) =>  {
            setIsLoading(true);
            const body = await res.json() as Map;
            await preloadImages([`${Client.getBaseUrl()}/api/image/${body.background}`]);
            dispatch(setMapId(id));
            dispatch(setXTiles(body.backgroundWidth));
            dispatch(setYTiles(body.backgroundHeight));
            dispatch(setMapValues({ ...body }));
            setIsLoading(false);
            return;
        }).catch(() => {
            // do nothing
        });
    }, [id]);

    const createSSERequest = () => {
        if(!guildId || !channelId) {
            return;
        }

        return Client.SSERequest(
            `/api/map/listen?guildId=${guildId ?? ''}&channelId=${channelId ?? ''}`,
            (x: string) => {
                const data: Partial<Map>
                    & { mapId?: string, ping?: { x: number, y: number } } = JSON.parse(x) as object;
                if(data.ping) {
                    // eslint-disable-next-line no-console
                    const _id = new ObjectId();
                    dispatch(addPing({ ...data.ping, _id }));
                    setTimeout(() => {
                        dispatch(removePing(_id));
                    }, 2000);
                }
                if(data.tokenLayer) {
                    dispatch(setMapValues({ ...data }));
                }
            },
        );
    };

    useEffect(() => {
        eventSourceRef.current = createSSERequest();

        eventSourceRef.current?.addEventListener('error' , () => {
            eventSourceRef.current?.close();
            eventSourceRef.current = createSSERequest();

        });

        return () => {
            eventSourceRef.current?.close();
        };
    }, [id, guildId, channelId]);

    return isLoading
        ? (
            <div className='m-auto flex grow justify-center align-center '>
                <Loader />
            </div>
        ) : (
            <MapEditor canEdit editors={[
                <ZoomEditor key="zoom" />,
                <HostToggle key="host" />,
                <EditorHotbar key="editorHotbar">
                    <TokenSelector />
                    <MapImageSelector />
                    <SpellSelector />
                </EditorHotbar>,
                <ToggleHotbar key="togglehotbar">
                    <SpellEffectEditor />
                    <MeasureEditor />
                    <FogEditor />
                    <DmLayerEditor />
                </ToggleHotbar>,
                <TurnOrderWidget key="turn-order-widget" canEdit />,
            ]}>
            </MapEditor>
        );
};
export default ViewMap;
